/**
 * sanitize.js — Centralized HTML sanitization helper.
 *
 * Use this for ALL dangerouslySetInnerHTML usages to prevent XSS.
 *
 * Two presets:
 *   - sanitizeStrict(html): tags allowed = b, strong, i, em, br, span, p, ul, ol, li.
 *     Use for short interpolated translation strings or counters.
 *   - sanitizeRich(html): allows additional safe formatting tags + minimal attributes
 *     (href / class / style limited). Use for backend-generated email HTML previews.
 *
 * Both presets:
 *   - Strip all event handlers (onclick, onerror, …)
 *   - Strip <script>, <iframe>, <object>, <embed>, <link>, <meta>
 *   - Force-target external links to noopener noreferrer
 *   - Disallow javascript:/data:/vbscript: URLs
 */
import DOMPurify from 'dompurify';

// Force-add rel="noopener noreferrer" + target="_blank" to all anchors
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
        node.setAttribute('rel', 'noopener noreferrer');
        node.setAttribute('target', '_blank');
    }
});

const STRICT_CONFIG = {
    ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'br', 'span', 'p', 'ul', 'ol', 'li', 'small'],
    ALLOWED_ATTR: ['class'],
    ALLOW_DATA_ATTR: false,
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'link', 'meta', 'style', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'srcdoc', 'formaction', 'action'],
};

const RICH_CONFIG = {
    ALLOWED_TAGS: [
        'a', 'b', 'strong', 'i', 'em', 'u', 'br', 'span', 'p', 'div',
        'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'small', 'blockquote', 'hr', 'pre', 'code',
    ],
    ALLOWED_ATTR: ['href', 'title', 'class', 'colspan', 'rowspan'],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'link', 'meta', 'style', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'srcdoc', 'formaction', 'action', 'style'],
};

/**
 * Strict sanitization for short, simple HTML content.
 * Returns an object suitable for dangerouslySetInnerHTML={ ... }.
 */
export function sanitizeStrict(html) {
    if (html === null || html === undefined) return { __html: '' };
    const clean = DOMPurify.sanitize(String(html), STRICT_CONFIG);
    return { __html: clean };
}

/**
 * Rich sanitization for backend-generated content (email HTML previews, etc.).
 * Strips scripts/iframes/event handlers but allows formatting and safe links.
 */
export function sanitizeRich(html) {
    if (html === null || html === undefined) return { __html: '' };
    const clean = DOMPurify.sanitize(String(html), RICH_CONFIG);
    return { __html: clean };
}

const _sanitize = { sanitizeStrict, sanitizeRich };
export default _sanitize;
